import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { GraduationCap, Loader2, Search, User, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { rpatPath, rpatStatus, attemptsText, pct } from '../rpat/assessmentResult';

const TONE_CLASS = {
  passed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  neutral: '',
};

const ResetButton = ({ row, busy, onReset }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50"
        disabled={busy || !row.attempts_used} data-testid={`rpat-reset-${row.assessment_id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" />Reset</>}
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Reset attempts for &quot;{row.assessment_title}&quot;?</AlertDialogTitle>
        <AlertDialogDescription>
          The learner gets {row.max_attempts ? `all ${row.max_attempts} attempts` : 'a fresh start'} back on this assessment.
          Their past scores stay in reports and history. Attempts used now: {attemptsText(row)}.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onReset} className="bg-amber-600 hover:bg-amber-700" data-testid={`rpat-reset-confirm-${row.assessment_id}`}>
          <RefreshCw className="h-4 w-4 mr-2" />Reset attempts
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const AttemptRow = ({ row, busy, onReset }) => {
  const status = rpatStatus(row);
  return (
    <div className="p-3 rounded-lg border flex items-start justify-between gap-3" data-testid={`rpat-attempt-row-${row.assessment_id}`}>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{rpatPath(row).slice(1).join(' › ')}</p>
        <p className="font-medium">{row.assessment_title}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
          <Badge variant="outline" className={TONE_CLASS[status.tone]} data-testid={`rpat-status-${row.assessment_id}`}>{status.label}</Badge>
          <span className="text-muted-foreground" data-testid={`rpat-attempts-${row.assessment_id}`}>Attempts {attemptsText(row)}</span>
          {row.best_score != null && <span className="text-muted-foreground">Best {pct(row.best_score)}</span>}
          {row.last_reset_at && <span className="text-xs text-muted-foreground">Last reset {new Date(row.last_reset_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <ResetButton row={row} busy={busy} onReset={onReset} />
    </div>
  );
};

/** RPATAttemptResetPanel - admin finds a learner and gives them fresh attempts on an R-PAT assessment */
const RPATAttemptResetPanel = ({ api }) => {
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = async (userId) => setData((await api.get(`/rpat/assessments/admin/user-attempts/${userId}`)).data);

  const search = async () => {
    const wanted = email.trim().toLowerCase();
    if (!wanted) return toast.error('Please enter a user email');
    setSearching(true);
    setData(null);
    try {
      const res = await api.get(`/users?email=${encodeURIComponent(wanted)}`);
      const found = (res.data.users || res.data || []).find(u => u.email?.toLowerCase() === wanted);
      if (!found) return toast.error('User not found');
      await load(found.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load R-PAT attempts');
    } finally {
      setSearching(false);
    }
  };

  const reset = async (row) => {
    setResetting(row.assessment_id);
    try {
      const res = await api.post(`/rpat/assessments/admin/reset-attempts/${data.user.id}/${row.assessment_id}`);
      toast.success(res.data.message, { description: 'The learner can take this assessment again.' });
      await load(data.user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset attempts');
    } finally {
      setResetting(null);
    }
  };

  return (
    <Card data-testid="rpat-attempt-reset-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5" />Reset R-PAT Assessment Attempts</CardTitle>
        <CardDescription>Search for a learner to see their R-PAT assessments and give them fresh attempts</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input placeholder="Enter user email..." value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()} data-testid="rpat-reset-email-input" />
          <Button onClick={search} disabled={searching} data-testid="rpat-reset-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg" data-testid="rpat-reset-user">
              <User className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="font-medium">{data.user.name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">{data.user.email}</p>
              </div>
              <Badge variant="outline" className="ml-auto">
                {data.assessments.length} assessment{data.assessments.length === 1 ? '' : 's'} attempted
              </Badge>
            </div>
            <ScrollArea className="h-[320px] pr-4">
              <div className="space-y-3">
                {data.assessments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8" data-testid="rpat-reset-empty">This learner has not attempted any R-PAT assessment yet</p>
                ) : data.assessments.map(row => (
                  <AttemptRow key={row.assessment_id} row={row} busy={resetting === row.assessment_id} onReset={() => reset(row)} />
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RPATAttemptResetPanel;
